import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Home as HomeIcon } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { fireGoogleAdsConversion } from "@/lib/analytics";

// Google Ads "Submit lead form" conversion fires from @/lib/analytics.js

const messagesBySource = {
  visit: {
    eyebrow: "Plan Your Visit",
    title: "We can't wait to meet you.",
    body:
      "Your visit request has been received. A member of our welcome team will reach out shortly with everything you need for Sunday.",
  },
  prayer: {
    eyebrow: "Prayer Request",
    title: "We are praying with you.",
    body:
      "Your request has been received and will be lifted up by our prayer team. You are not alone — God hears you, and we stand with you.",
  },
  contact: {
    eyebrow: "Message Received",
    title: "Thank you for reaching out.",
    body:
      "We've received your message and someone from our team will be in touch soon.",
  },
  donation: {
    eyebrow: "Thank You",
    title: "Your generosity blesses this house.",
    body:
      "Thank you for your intent to give. Every gift is stewarded with integrity and goes directly to the ministry of the gospel through New Philadelphia.",
  },
  newsletter: {
    eyebrow: "Subscribed",
    title: "You're in the family.",
    body:
      "Thanks for subscribing. Look out for encouragement, event updates, and word from Pastor Little.",
  },
  default: {
    eyebrow: "Thank You",
    title: "Received with gratitude.",
    body:
      "Thanks for connecting with New Philadelphia. Someone from our team will follow up soon.",
  },
};

export default function ThankYou() {
  useReveal();
  const [params] = useSearchParams();
  const source = params.get("source") || "default";
  const content = useMemo(
    () => messagesBySource[source] || messagesBySource.default,
    [source]
  );

  // Fire the Google Ads conversion event once on mount.
  useEffect(() => {
    fireGoogleAdsConversion(source);
  }, [source]);

  return (
    <div data-testid="thank-you-page" className="relative min-h-[80vh] bg-npbc-cream">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-npbc-red/[0.04] via-white to-npbc-gold/[0.05]" />
        <div className="container-npbc relative py-28 md:py-36">
          <div className="mx-auto max-w-2xl text-center" data-reveal>
            <div className="mx-auto inline-flex h-20 w-20 items-center justify-center rounded-full bg-npbc-gold/15 ring-8 ring-npbc-gold/[0.06]">
              <CheckCircle2 className="h-10 w-10 text-npbc-red" strokeWidth={1.6} />
            </div>
            <div
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.28em] text-npbc-red backdrop-blur"
              data-testid="thank-you-eyebrow"
            >
              {content.eyebrow}
            </div>
            <h1
              className="mt-6 heading-serif text-5xl md:text-6xl leading-[1.05] text-npbc-black"
              data-testid="thank-you-title"
            >
              {content.title}
            </h1>
            <p
              className="mt-6 text-npbc-gray/85 text-lg leading-relaxed"
              data-testid="thank-you-body"
            >
              {content.body}
            </p>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                data-testid="thank-you-home-btn"
                className="btn-primary !py-3 !px-6 text-sm"
              >
                <HomeIcon className="h-4 w-4" />
                Back to Home
              </Link>
              <Link
                to="/visit"
                data-testid="thank-you-visit-btn"
                className="btn-ghost-dark !py-3 !px-6 text-sm"
              >
                Plan Another Visit
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <p className="mt-14 text-xs uppercase tracking-[0.28em] text-npbc-gray/60">
              Sunday Worship · 11:00 AM · 533 S. Mendenhall Rd.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
